import { DropboxService } from '@aipb/gulliver/dropbox/data-access';
import {
  DenormalizedFormat,
  FormatSize,
  ImageFormatParams,
  WatermarkPosition,
  WatermarkSource
} from '@aipb/gulliver/dropbox/model';
import { saveFileFromUrl } from '@aipb/shared/util/other';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgFormsManager } from '@ngneat/forms-manager';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { finalize, mergeMap } from 'rxjs/operators';
import { ImagePreviewOpenLayersService, TOOL_FREE } from '../../services/image-preview-open-layers.service';

const FORM_NAME = 'image-preview-crop';

interface AppForms {
  [FORM_NAME]: ImagePreviewCropForm;
}

interface ImagePreviewCropForm {
  format: DenormalizedFormat;
  formatSize: FormatSize;
  watermarkSource: WatermarkSource;
  watermarkPosition: WatermarkPosition;
}

@UntilDestroy()
@Component({
  selector: 'ip-crop-form',
  templateUrl: './crop-form.component.html',
  styleUrls: ['./crop-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CropFormComponent implements OnInit, OnChanges, OnDestroy {
  @Input() id: string;
  @Input() formats: DenormalizedFormat[] = [];
  @Input() watermarkSources: WatermarkSource[] = [];
  @Input() watermarkPositions: WatermarkPosition[] = [];

  cropForm: FormGroup;

  formatSizes$: Observable<FormatSize[]> = this.formsManager.valueChanges<DenormalizedFormat>(FORM_NAME, 'format').pipe(
    mergeMap((format) => {
      if (!format) {
        this.imagePreviewOpenLayers.initTool(null);
        this.formsManager.patchValue(FORM_NAME, {
          formatSize: null
        });
        return of([]);
      }

      this.imagePreviewOpenLayers.initTool(format.cropRatio[0]);

      const formatSize: FormatSize = this.cropForm.controls.formatSize.value;

      if (formatSize) {
        this.formsManager.patchValue(FORM_NAME, {
          formatSize: format.sizes.some(
            (fs) => fs.size === formatSize.size && fs.watermark.size === formatSize.watermark.size
          )
            ? formatSize
            : null
        });
      }

      return of(format.sizes);
    })
  );
  watermarkSources$: Observable<WatermarkSource[]> = this.formsManager
    .valueChanges<FormatSize>(FORM_NAME, 'formatSize')
    .pipe(
      mergeMap((data) => {
        if (!data) {
          this.formsManager.patchValue(FORM_NAME, {
            watermarkSource: null
          });
          return of([]);
        }

        const watermarkSources = data.watermark.source.map((data) => {
          return this.watermarkSources.find((watermarkSource) => watermarkSource.id === data);
        });

        const watermarkSource: WatermarkSource = this.cropForm.controls.watermarkSource.value;

        if (watermarkSource) {
          this.formsManager.patchValue(FORM_NAME, {
            watermarkSource: watermarkSources.some((ws) => ws.id === watermarkSource.id) ? watermarkSource : null
          });
        }

        return watermarkSources ? of(watermarkSources) : of([]);
      })
    );
  watermarkPositions$: Observable<WatermarkPosition[]> = this.formsManager
    .valueChanges<FormatSize>(FORM_NAME, 'formatSize')
    .pipe(
      mergeMap((data) => {
        if (!data) {
          this.formsManager.patchValue(FORM_NAME, {
            watermarkPosition: null
          });
          return of([]);
        }

        const watermarkPositions = data.watermark.position.map((data) =>
          this.watermarkPositions.find((watermarkPosition) => watermarkPosition.id === data)
        );

        const watermarkPosition: WatermarkPosition = this.cropForm.controls.watermarkPosition.value;

        if (watermarkPosition) {
          this.formsManager.patchValue(FORM_NAME, {
            watermarkPosition: watermarkPositions.some((wp) => wp.id === watermarkPosition.id)
              ? watermarkPosition
              : null
          });
        }

        return watermarkPositions ? of(watermarkPositions) : of([]);
      })
    );
  private _loading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public loading$: Observable<boolean> = this._loading.asObservable();

  constructor(
    private formsManager: NgFormsManager<AppForms>,
    private builder: FormBuilder,
    private imagePreviewOpenLayers: ImagePreviewOpenLayersService,
    private dropbox: DropboxService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.cropForm = this.builder.group({
      format: [null, Validators.required],
      formatSize: [null, Validators.required],
      watermarkSource: [null, Validators.required],
      watermarkPosition: [null]
    });

    this.formsManager.upsert(FORM_NAME, this.cropForm, {
      persistState: true
    });

    this.formsManager
      .valueChanges<WatermarkSource>(FORM_NAME, 'watermarkSource')
      .pipe(untilDestroyed(this))
      .subscribe((watermarkSource) => {
        if (watermarkSource && watermarkSource.id === 'none') {
          this.cropForm.controls['watermarkPosition'].setValidators(null);
        } else {
          this.cropForm.controls['watermarkPosition'].setValidators([Validators.required]);
        }

        this.cropForm.controls['watermarkPosition'].updateValueAndValidity();
        this.cdr.detectChanges();
      });

    this.formsManager
      .valueChanges(FORM_NAME, 'format')
      .pipe(untilDestroyed(this))
      .subscribe((format) => {
        if (!format) return;

        const cropRatio = format.cropRatio[0];

        const formatSize = format.sizes && format.sizes.length > 0 ? format.sizes[0] : null;

        if (cropRatio === TOOL_FREE && formatSize) {
          const watermarkSource = this.watermarkSources.find((ws) => ws.id === formatSize.watermark.source[0]);
          const watermarkPosition = this.watermarkPositions.find((wp) => wp.id === formatSize.watermark.position[0]);

          this.formsManager.patchValue(FORM_NAME, {
            formatSize: formatSize,
            watermarkSource: watermarkSource,
            watermarkPosition: watermarkPosition
          });
        }
      });
  }

  ngOnChanges() {
    if (!this.cropForm) return;

    this.formsManager.reset(FORM_NAME);
  }

  onDownload(): void {
    if (!this.cropForm.valid) return;

    const value: ImagePreviewCropForm = this.cropForm.value;

    const params: ImageFormatParams = {
      res: this.id,
      format: value.format.id,
      width: this.imagePreviewOpenLayers.imageWidth,
      height: this.imagePreviewOpenLayers.imageHeight
    };

    if (value.formatSize.size) {
      params.size = value.formatSize.size;
    }

    if (value?.watermarkPosition?.id) {
      params.wPosition = value.watermarkPosition.id;
    }

    if (value?.formatSize?.watermark?.size) {
      params.wSize = value.formatSize.watermark.size;
    }

    if (value?.watermarkSource?.id) {
      params.wSource = value.watermarkSource.id;
    }

    const feature = this.imagePreviewOpenLayers.getActiveDraw();

    if (feature) {
      params.crop = feature.getGeometry().getCoordinates().toString();
    }

    this._loading.next(true);
    this.dropbox
      .imageFormat(params)
      .pipe(
        finalize(() => {
          this._loading.next(false);
        })
      )
      .subscribe((data) => {
        const image = URL.createObjectURL(data[0]);
        saveFileFromUrl(image, data[1][0], data[1][1]);
      });
  }

  compareFormats(f1: DenormalizedFormat, f2: DenormalizedFormat): boolean {
    if (f1 && f2) {
      return f1.id === f2.id;
    }
    return false;
  }

  compareFormatSizes(f1: FormatSize, f2: FormatSize): boolean {
    if (f1 && f2) {
      return f1.size === f2.size && f1.watermark.size === f2.watermark.size;
    }
    return false;
  }

  compareWatermarkSources(f1: WatermarkSource, f2: WatermarkSource): boolean {
    if (f1 && f2) {
      return f1.id === f2.id;
    }
    return false;
  }

  compareWatermarkPositions(f1: WatermarkPosition, f2: WatermarkPosition): boolean {
    if (f1 && f2) {
      return f1.id === f2.id;
    }
    return false;
  }

  ngOnDestroy() {
    this.formsManager.unsubscribe(FORM_NAME);
  }
}
